import type React from 'react';

import type {
    BlockOpenAPIOperationData,
    BlockOpenAPISchemasData,
    BlockOpenAPIWebhookData,
} from '@gitbook/doc-core';
import type { Filesystem } from '@gitbook/openapi-parser';
import { createPalette, PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';

import { useNavigation } from '../../../navigation';
import { useIsBlockHovered } from '../common/layout/hovered';
import { ReferenceBlockActions, ReferenceBlockFrame } from '../ReferenceBlockFrame';
import { OpenAPIBlockWrapper, type OpenAPIBlockWrapperProps } from './OpenAPIBlockWrapper';
import { OpenAPIFetch } from './OpenAPIFetch';

interface BlockOpenAPIWrapperProps<T> extends Omit<OpenAPIBlockWrapperProps, 'children'> {
    data: T;
    children: (props: { specUrl: string; filesystem: Filesystem }) => React.ReactNode;
}

/**
 * Wrapper component for OpenAPI blocks.
 * It fetches the OpenAPI specification and provides it to the children.
 * It also renders the OpenAPI block frame and actions.
 */
export function BlockOpenAPIFetchWrapper<
    T extends BlockOpenAPIOperationData | BlockOpenAPISchemasData | BlockOpenAPIWebhookData,
>(props: BlockOpenAPIWrapperProps<T>) {
    const { node, editor, isFocused, children, data } = props;

    return (
        <OpenAPIBlockWrapper {...props}>
            <OpenAPIFetch data={data}>
                {({ specUrl, filesystem, organizationId }) => (
                    <>
                        {!editor.readOnly && (
                            <BlockOpenAPIFrame
                                specSlug={data.ref.spec}
                                isFocused={isFocused}
                                blockKey={node.key}
                                organizationId={organizationId}
                            />
                        )}
                        {children({
                            specUrl,
                            filesystem,
                        })}
                    </>
                )}
            </OpenAPIFetch>
        </OpenAPIBlockWrapper>
    );
}

function BlockOpenAPIFrame(props: {
    isFocused: boolean;
    blockKey: string;
    specSlug: string;
    organizationId: string;
}) {
    const { blockKey, isFocused, specSlug, organizationId } = props;
    const isHovered = useIsBlockHovered(blockKey);
    const { navigate } = useNavigation();
    return (
        <ReferenceBlockFrame isFocused={isFocused} isHovered={isHovered}>
            <ReferenceBlockActions>
                <PaletteButton
                    leadingIcon={Icon.OpenAPI}
                    size="small"
                    label={specSlug}
                    palette={() => {
                        return createPalette({
                            groups: [
                                {
                                    key: 'openapi',
                                    commands: [
                                        {
                                            key: 'view',
                                            icon: Icon.OpenAPI,
                                            title: 'View specification',
                                            trigger: () => {
                                                navigate({
                                                    screen: {
                                                        type: 'openapi_spec',
                                                        spec: specSlug,
                                                        organization: organizationId,
                                                    },
                                                });
                                            },
                                        },
                                    ],
                                },
                            ],
                        });
                    }}
                />
            </ReferenceBlockActions>
        </ReferenceBlockFrame>
    );
}
